import { useState, useEffect, useLayoutEffect, useRef, useCallback, CSSProperties } from 'react';

/**
 * Shared positioning for all form dropdowns (PareigybeSelect, MultiSelect,
 * InstitutionCombobox). The panel is rendered in a portal (see FloatingPortal in
 * the consumers) and positioned here with deterministic fixed coordinates from
 * the trigger's bounding rect:
 *  - floats over the modal (no overflow clipping, never pushes the Save button),
 *  - opens downward by default and flips up when there isn't room below,
 *  - caps height to the available space and scrolls internally,
 *  - re-positions on scroll/resize,
 *  - dismisses on outside-click / Escape (ignoring clicks on the panel itself).
 *
 * Same return shape as a Floating UI hook so consumers stay unchanged:
 * { refs.setReference, refs.setFloating, floatingStyles, getReferenceProps, getFloatingProps }.
 */
export function useAnchoredDropdown(open: boolean, onOpenChange: (o: boolean) => void) {
  const referenceRef = useRef<HTMLElement | null>(null);
  const floatingRef = useRef<HTMLElement | null>(null);
  const [style, setStyle] = useState<CSSProperties>({ position: 'fixed', top: -9999, left: 0, visibility: 'hidden' });

  const update = useCallback(() => {
    const ref = referenceRef.current;
    if (!ref) return;
    const r = ref.getBoundingClientRect();
    const margin = 8;
    const gap = 4;
    const vh = window.innerHeight;
    const spaceBelow = vh - r.bottom - margin;
    const spaceAbove = r.top - margin;
    const cap = 340;
    // Flip up only when below is too small AND above has more room.
    const openUp = spaceBelow < Math.min(cap, 220) && spaceAbove > spaceBelow;
    const maxHeight = Math.max(160, Math.min(cap, (openUp ? spaceAbove : spaceBelow) - gap));
    setStyle({
      position: 'fixed',
      left: Math.round(r.left),
      width: Math.round(r.width),
      maxHeight,
      zIndex: 1100,
      // overflow is owned by each panel's own CSS (some pin a search header and
      // scroll only the list); we only cap the height here.
      ...(openUp
        ? { bottom: Math.round(vh - r.top + gap) }
        : { top: Math.round(r.bottom + gap) }),
    });
  }, []);

  // Position before paint once the panel is mounted, and keep it in sync.
  useLayoutEffect(() => {
    if (!open) return;
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, update]);

  // Outside-click + Escape dismissal (ignores clicks inside the trigger or panel).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (referenceRef.current?.contains(t) || floatingRef.current?.contains(t)) return;
      onOpenChange(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onOpenChange(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onOpenChange]);

  const setReference = useCallback((el: HTMLElement | null) => { referenceRef.current = el; }, []);
  const setFloating = useCallback((el: HTMLElement | null) => { floatingRef.current = el; }, []);

  return {
    refs: { setReference, setFloating },
    floatingStyles: style,
    getReferenceProps: () => ({}),
    getFloatingProps: () => ({}),
  };
}
